import type { PatientCommunicationPreferencesClientRow } from '@helios/data-access';

/* ------------------------------------------------------------------ */
/*  Patient WhatsApp opt-in write helper (PRD.222)                    */
/*                                                                    */
/*  Writes go to the local PowerSync DB via db.execute(), which the   */
/*  upload pipeline POSTs to syncWrite — never direct SQL to Postgres.*/
/*  syncWrite owns the (org_id, patient_id) upsert + updated_by /      */
/*  updated_at injection; this helper only shapes the local mutation. */
/* ------------------------------------------------------------------ */

/** Minimal structural type for the PowerSync db handle's write method. */
export interface PowerSyncWritable {
  execute: (sql: string, params?: unknown[]) => Promise<unknown>;
}

export interface SetWhatsAppOptInInput {
  /** PowerSync db handle (from usePowerSyncDb). */
  db: PowerSyncWritable;
  orgId: string;
  patientId: string;
  /** Existing prefs row for this patient, or null if none exists yet. */
  existingRow: PatientCommunicationPreferencesClientRow | null;
  /** Desired state — true = opt in, false = opt out. */
  optIn: boolean;
  /**
   * Required when `optIn` is false — stamped onto whatsapp_opt_out_reason.
   * Ignored when opting in.
   */
  optOutReason?: string;
}

export class OptOutReasonRequiredError extends Error {
  constructor() {
    super('A reason is required when disabling WhatsApp messaging.');
    this.name = 'OptOutReasonRequiredError';
  }
}

/**
 * Enable or disable a patient's WhatsApp opt-in.
 *
 * Behaviour (PRD.222 rules 9, 10, 12, 13):
 * - Enable  → whatsapp_opt_in = 1, opt-out fields cleared.
 * - Disable → whatsapp_opt_in = 0, whatsapp_opt_out_at stamped, reason required.
 * - Enable with no existing row → INSERT a new row.
 * - Disable with no existing row → no-op (already not opted in).
 *
 * Returns `true` if a write was issued, `false` if it was a no-op.
 */
export async function setWhatsAppOptIn(input: SetWhatsAppOptInInput): Promise<boolean> {
  const { db, orgId, patientId, existingRow, optIn } = input;

  if (!optIn) {
    const reason = input.optOutReason?.trim();
    if (!reason) throw new OptOutReasonRequiredError();

    // Disable with no row = already not opted in → nothing to do.
    if (!existingRow) return false;

    await db.execute(
      `UPDATE patient_communication_preferences
       SET whatsapp_opt_in = 0,
           whatsapp_opt_out_at = ?,
           whatsapp_opt_out_reason = ?
       WHERE id = ?`,
      [new Date().toISOString(), reason, existingRow.id],
    );
    return true;
  }

  // Enable — clear opt-out fields.
  if (existingRow) {
    await db.execute(
      `UPDATE patient_communication_preferences
       SET whatsapp_opt_in = 1,
           whatsapp_opt_out_at = NULL,
           whatsapp_opt_out_reason = NULL
       WHERE id = ?`,
      [existingRow.id],
    );
    return true;
  }

  // Enable with no existing row → create it. sms_opt_in / email_opt_in fall
  // back to their Postgres defaults (false); extra/created_at/updated_at use
  // column defaults; updated_by/updated_at are server-injected by syncWrite.
  await db.execute(
    `INSERT INTO patient_communication_preferences
       (id, org_id, patient_id, whatsapp_opt_in)
     VALUES (?, ?, ?, 1)`,
    [crypto.randomUUID(), orgId, patientId],
  );
  return true;
}

/* ------------------------------------------------------------------ */
/*  Derived display state                                             */
/* ------------------------------------------------------------------ */

export type WhatsAppOptInState = 'opted-in' | 'opted-out' | 'not-set';

/** Resolve a prefs row (or its absence) into a display state. */
export function whatsAppOptInState(
  row: PatientCommunicationPreferencesClientRow | null,
): WhatsAppOptInState {
  if (!row) return 'not-set';
  if (row.whatsapp_opt_in === 1) return 'opted-in';
  // opt_in = 0 with an opt-out timestamp = explicitly opted out;
  // opt_in = 0 with no timestamp = a row that was never opted in.
  return row.whatsapp_opt_out_at ? 'opted-out' : 'not-set';
}
